'use client';
import { useEffect, useRef, useState } from 'react';
import type { TableOfContentsEntry } from './types';
import { getScrollProgress } from './utils';

const MIN_SCROLL_DELTA = 1;

type MobileTocBarState = {
  isScrolled: boolean;
  isRowVisible: boolean;
  progress: number;
  activeTarget: string;
};

function getContentBottom(): number {
  const main = document.getElementById('main');

  if (!main) {
    return document.documentElement.scrollHeight;
  }

  return main.getBoundingClientRect().bottom + window.scrollY;
}

function hasReachedArticleEnd(): boolean {
  const contentBottom = getContentBottom();

  if (contentBottom <= window.innerHeight) {
    return false;
  }

  return contentBottom <= window.scrollY + window.innerHeight;
}

function getActiveTarget(items: TableOfContentsEntry[], scrollOffset: number): string {
  const lastItem = items[items.length - 1];

  if (lastItem && hasReachedArticleEnd()) {
    return lastItem.target;
  }

  let activeTarget = '';

  for (const item of items) {
    const heading = document.getElementById(item.target);

    if (heading && heading.getBoundingClientRect().top <= scrollOffset) {
      activeTarget = item.target;
    }
  }

  return activeTarget;
}

export function useMobileTocBar(
  items: TableOfContentsEntry[],
  scrollOffset: number,
): MobileTocBarState {
  const [state, setState] = useState<MobileTocBarState>({
    isScrolled: false,
    isRowVisible: true,
    progress: 0,
    activeTarget: '',
  });
  const itemsRef = useRef(items);
  const scrollOffsetRef = useRef(scrollOffset);
  itemsRef.current = items;
  scrollOffsetRef.current = scrollOffset;

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let frame = 0;

    const update = () => {
      frame = 0;
      const scrollY = window.scrollY;
      const delta = scrollY - lastScrollY;
      const hasMoved = Math.abs(delta) >= MIN_SCROLL_DELTA;
      const isScrolled = scrollY > 0;
      const progress =
        Math.round(getScrollProgress(scrollY, getContentBottom(), window.innerHeight) * 10) / 10;
      const activeTarget = getActiveTarget(itemsRef.current, scrollOffsetRef.current);

      if (hasMoved) {
        lastScrollY = scrollY;
      }

      setState(prev => {
        const isRowVisible = hasMoved ? delta < 0 : prev.isRowVisible;

        if (
          prev.isScrolled === isScrolled &&
          prev.isRowVisible === isRowVisible &&
          prev.progress === progress &&
          prev.activeTarget === activeTarget
        ) {
          return prev;
        }

        return { isScrolled, isRowVisible, progress, activeTarget };
      });
    };

    const schedule = () => {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    schedule();

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);

      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  return state;
}
